import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { useCallback, useRef, useState } from 'react';
import { HeaderConfig } from '@v2/pages/common/components/ContextHeader/ContextHeader';
import { formatSinceTime, localTime, parseRouteParamAsNumber } from '@helpers';
import { NCFileSearchItem, PartSearchItem, ProductionBatchStatistics } from '@interfaces/search';
import { useSelector } from 'react-redux';
import { setBatch, setBatchId, setBatchLoadedTime } from '@reducers/batch';
import { selectForceUpdateHeaderConfig, setJobId, setNcFileData, setNcFileId, setPartId, setParts } from '@reducers/details';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { PaginatedResponse } from '@interfaces/machines';
import { GetNCFileById } from '@interfaces/methods';

interface UseBatchDetailsProps {
  batchId?: number;
  productionBatchStatistics: (param: { productionBatchId: number }) => Promise<{ statistics: ProductionBatchStatistics }>;
  getNCFileById?: GetNCFileById;
  getPartByID?: (partId: number) => Promise<PaginatedResponse<PartSearchItem>>;
  getTools?: (param: { ncFileId: number }) => Promise<any[]>;
  clearTools?: () => void;
  updateHeaderConfig?: (headerConfig: HeaderConfig) => void;
  shouldLoadNcFile?: boolean;
}

export const useBatchDetails = ({
  batchId: _batchId,
  productionBatchStatistics,
  getNCFileById,
  getTools,
  clearTools,
  getPartByID,
  shouldLoadNcFile = true,
  updateHeaderConfig,
}: UseBatchDetailsProps) => {
  const route = useRoute();
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const forceUpdateHeaderConfig = useSelector(selectForceUpdateHeaderConfig);

  const [ncFileDataLoading, setNcFileDataLoading] = useState<boolean>(true);
  const [partDataLoading, setPartDataLoading] = useState<boolean>(true);

  const ncFileIdRef = useRef<number | null>(null);

  const batchId = _batchId ?? parseRouteParamAsNumber(route.params?.batchId);

  const loadAndSetNcFileData = async (ncFileId: number | null | undefined) => {
    if (ncFileId == null || !shouldLoadNcFile) {
      setNcFileDataLoading(false);
      return;
    }
    const ncFileData = getNCFileById ? await getNCFileById(ncFileId) : null;
    if (ncFileData) {
      dispatch(setNcFileId(ncFileData?.ncFileId));
      dispatch(setNcFileData(ncFileData));
    }
    setNcFileDataLoading(false);
    return ncFileData;
  };

  const loadAndSetBatch = async () => {
    if (!batchId) return;

    dispatch(setBatchId(batchId));
    const batchStatistic = await productionBatchStatistics({ productionBatchId: batchId });
    if (batchStatistic?.statistics) {
      dispatch(setBatch(batchStatistic.statistics));
      if (!route.params?.batchNumber || !route.params?.ncFileStatus) {
        navigation.setParams({ batchNumber: batchStatistic.statistics.batchNumber, ncFileStatus: batchStatistic.statistics.ncFileStatus });
      }
    }
    dispatch(setBatchLoadedTime(formatSinceTime(localTime().format()).toLowerCase()));
    return batchStatistic;
  };

  const loadAndSetParts = async (partId: number | undefined | null) => {
    if (!partId || !getPartByID) {
      setPartDataLoading(false);
      dispatch(setParts([]));
      return;
    }
    const partDetails: PaginatedResponse<PartSearchItem> = await getPartByID(partId);
    if (partDetails.searchResult.length) {
      dispatch(setParts([partDetails.searchResult[0]]));
      dispatch(setPartId(partDetails.searchResult?.[0]?.partId));
    }
    setPartDataLoading(false);
    return partDetails;
  };

  const loadDataAndDefineHeader = async () => {
    const batchData = await loadAndSetBatch();

    const ncFileId = batchData?.statistics?.ncFileId;
    const ncFileData = await loadAndSetNcFileData(ncFileId);

    let toolsLength = 0;

    if (ncFileId && getTools) {
      ncFileIdRef.current = ncFileId;
      const tools = await getTools({ ncFileId });
      toolsLength = tools?.length;
    } else if (clearTools) {
      await clearTools();
    }

    const links = Array.from(new Map(batchData?.statistics?.links?.map((link) => [link.partId, link])).values());

    const singlePartId = links?.[0]?.partId;

    const batchNotLinked = links?.length === 0;
    const singleLink = links?.length === 1;

    if (links?.length > 0) {
      setPartDataLoading(false);
    }

    if (singleLink) {
      dispatch(setPartId(singlePartId));
    }

    if (batchNotLinked) {
      await loadAndSetParts(ncFileData?.partId);
    }

    const partId = batchNotLinked ? ncFileData?.partId : singleLink ? singlePartId : '';
    const partName = batchNotLinked ? ncFileData?.partNumber : singleLink ? links?.[0]?.partName : `#${links.length}`;

    const partHeaderConfig = { id: partId ?? '', name: partName ?? '' };

    const ncFileHeaderConfig = {
      ncFile: ncFileData?.ncFileName ?? '',
      ncFileId: ncFileData?.ncFileId ?? '',
      ncFileStatus: ncFileData?.ncFileStatus ?? '',
    };

    const batchLinks = batchData?.statistics?.links?.length ?? 0;
    const batchHasSingleLink = batchLinks === 1;
    const firstLink = batchData?.statistics?.links?.[0];

    const jobs = batchHasSingleLink ? `${firstLink?.productionOrderNumber}-${firstLink?.productionOperationNumber}` : `#${batchLinks ?? 0}`;

    if (batchHasSingleLink) {
      dispatch(setJobId(firstLink?.productionOperationId || null));
    }

    if (updateHeaderConfig) {
      updateHeaderConfig({
        job: jobs,
        batch: batchData?.statistics?.batchNumber ?? '',
        ...ncFileHeaderConfig,
        part: partHeaderConfig,
        tools: `#${toolsLength}`,
        productionOrder: '',
      });
    }
  };

  useFocusEffect(
    useCallback(() => {
      let isActive = true;

      const fetchData = async () => {
        if (isActive) {
          await loadDataAndDefineHeader();
        }
      };

      fetchData();

      return () => {
        isActive = false;
      };
    }, [batchId, forceUpdateHeaderConfig])
  );

  const refetchTools = useCallback(() => {
    if (ncFileIdRef.current && getTools) {
      getTools({ ncFileId: ncFileIdRef.current });
    }
  }, [getTools]);

  return {
    loadBatch: loadAndSetBatch,
    batchId,
    ncFileDataLoading,
    partDataLoading,
    refetchTools,
  };
};
